import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil, isEmpty } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectUserActivePartnerTimeZone } from 'store/slices/userData';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useMenuItem } from 'hooks/UseMenuItem';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { TemplateHttpService, UtilsHttpService } from 'services/http';
import { getExpirationData } from 'services/template';
// Import Helpers
import { getTemplateChannelIconWithLabel } from 'services/Helpers';
// Import Components
import AccessControl from 'components/common/AccessControl';
import UsedInSection from 'components/common/UsedInSection';
// Import UI Components
import { Tooltip, SkeletonLoader } from '@geneui/components';
// Import Constants
import { TemplateTypesLabels, l, PARTNER_SETTINGS, ExpirationTypes } from 'constants/common';
// Import Enums
import { MenuItemIdEnum } from 'enums';
// Import Contexts
import { useVersionHistoryContext } from 'components/VersionHistory';

const { CMSPopUp, PopUp, DynamicPopUp } = TemplateTypesLabels;

const getMenuItemId = (type) => {
    switch (type) {
        case CMSPopUp:
            return MenuItemIdEnum.CMSPopUpNotification;
        case PopUp:
            return MenuItemIdEnum.PopUpNotification;
        default:
            return;
    }
};

const GeneralInfo = (props) => {
    const { templateId, isPredefined, name, description, type, additional, templateCategoryIds, predefinedKeyValue } =
        props;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { pointInTime } = useVersionHistoryContext();
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    const partnerTimeZone = useAppSelector(selectUserActivePartnerTimeZone) ?? 0;

    const [urlTypesData, setUrlTypesData] = useState<Nullable<Record<string, string>>>(null);

    const menuItemId = getMenuItemId(type);
    const { convertedData, isLoading, isLoadingConfig, restore } = useMenuItem(menuItemId, false);

    const { convertedData: templateCategoryData, isLoading: isLoadingTemplateCategory } = useMenuItem(
        templateCategoryIds ? MenuItemIdEnum.TemplateCategory : null,
    );

    const { getCampaignsByTemplate, getPopUpComparisons } = useCancelableRequests({
        getCampaignsByTemplate: TemplateHttpService.getCampaignsByTemplate,
        getPopUpComparisons: UtilsHttpService.getPopUpComparisons,
    });

    const [isLoadingUsedIn, setIsLoadingUsedIn] = useState(false);
    const [usedInCampaigns, setUsedInCampaigns] = useState(null);

    const getUsages = () => {
        setIsLoadingUsedIn(true);
        doGetRequest(getCampaignsByTemplate.request, {
            queryString: { templateId, pointInTime },
            successCallback: (data) => {
                setUsedInCampaigns(data);
                setIsLoadingUsedIn(false);
            },
        });
    };

    const getUrlComparisonTypes = () => {
        doGetRequest(getPopUpComparisons.request, {
            successCallback: (data) => {
                const popUpNotificationUrlComparisons = data.reduce(
                    (acc, { Id, Name }) => ({ ...acc, [Id]: Name }),
                    {},
                );
                setUrlTypesData(popUpNotificationUrlComparisons);
            },
        });
    };

    const init = () => {
        if (!isPredefined) {
            getUsages();
        }

        if (!isPredefined && type === PopUp) {
            getUrlComparisonTypes();
            restore([+additional.Id]);
        }

        if (type === CMSPopUp && !isNil(additional?.Alias)) {
            restore([+additional.Alias]);
        }
    };

    const destroyComponent = () => {
        return () => {
            setIsLoadingUsedIn(false);
            setUsedInCampaigns(null);
        };
    };
    const templateCategoryNames = useMemo(() => {
        if (isNil(templateCategoryIds) || isEmpty(templateCategoryIds) || isNil(templateCategoryData)) {
            return t(l.NA);
        }

        const categoryNames = templateCategoryData
            .filter((templateCategory) => {
                return templateCategoryIds.includes(+templateCategory.value);
            })
            .map((tempCategory) => tempCategory.label);

        return categoryNames.join(', ');
    }, [t, templateCategoryData, templateCategoryIds]);

    const expiration = useMemo(() => {
        if (isNil(additional) || type !== DynamicPopUp) return null;
        const { expirationType, expirationPeriod, expirationDateTime, timezone } = getExpirationData(
            additional,
            partnerTimeZone,
            partnerDateTimeFormat,
        );
        return {
            expirationType,
            expirationPeriod,
            expirationDateTime,
            timezone,
        };
    }, [additional, partnerDateTimeFormat, partnerTimeZone, type]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(destroyComponent, []);

    return (
        <div className="crm-object-modal-head">
            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.GeneralInfo)}</p>
                <span className="title-wrapper-line" />
            </div>

            <Tooltip text={templateId} position="bottom">
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.TemplateId)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{templateId}</span>
                </div>
            </Tooltip>

            <Tooltip text={name} position="bottom">
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Title)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{name}</span>
                </div>
            </Tooltip>

            <Tooltip text={description} position="bottom">
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Description)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {isEmpty(description) ? t(l.NA) : description}
                    </span>
                </div>
            </Tooltip>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Type)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {<div className="crm-template-type-label">{getTemplateChannelIconWithLabel(type)}</div>}
                </span>
            </div>
            {predefinedKeyValue && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Regions)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{predefinedKeyValue.regions}</span>
                </div>
            )}
            {predefinedKeyValue && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Categories)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{predefinedKeyValue.categories}</span>
                </div>
            )}

            <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowTemplateCategory}>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.TemplateCategory)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        <SkeletonLoader isBusy={isLoadingTemplateCategory && !isNil(templateCategoryIds)}>
                            {<div className="crm-template-type-label">{templateCategoryNames}</div>}
                        </SkeletonLoader>
                    </span>
                </div>
            </AccessControl>

            {type === CMSPopUp && !isNil(additional?.Alias) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.CMSPopUp)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        <SkeletonLoader isBusy={isLoading || isLoadingConfig}>
                            {convertedData.find((item) => item.value.toString() === additional.Alias)?.label ?? t(l.NA)}
                        </SkeletonLoader>
                    </span>
                </div>
            )}

            {type === PopUp && !isNil(additional) && (
                <>
                    <Tooltip text={additional.Alias} position="bottom">
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Name)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                <SkeletonLoader isBusy={isLoading || isLoadingConfig}>
                                    {convertedData.find((item) => item.value.toString() === additional.Id)?.label ??
                                        t(l.NA)}
                                </SkeletonLoader>
                            </span>
                        </div>
                    </Tooltip>

                    <Tooltip text={urlTypesData?.[additional.Page.Comparison]} position="bottom">
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Operator)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                <SkeletonLoader isBusy={isNil(urlTypesData)}>
                                    {urlTypesData?.[additional.Page.Comparison] ?? t(l.NA)}
                                </SkeletonLoader>
                            </span>
                        </div>
                    </Tooltip>

                    <Tooltip text={additional.Page.Value} position="bottom">
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Route)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {isEmpty(additional.Page.Value) ? t(l.NA) : additional.Page.Value}
                            </span>
                        </div>
                    </Tooltip>
                </>
            )}

            <UsedInSection
                isLoadingUsedIn={isLoadingUsedIn}
                usedInData={usedInCampaigns}
                isVisible={!isPredefined}
                notUsedInDisplayNameKey={l.TemplateIsNotUsed}
            />

            {expiration && expiration.expirationType && (
                <>
                    <Tooltip text={expiration.expirationType} position="bottom">
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.ExpirationType)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {expiration.expirationType === ExpirationTypes.ExpirationPeriod
                                    ? t(l.FromSentDate)
                                    : t(l.ToGivenDate)}
                            </span>
                        </div>
                    </Tooltip>

                    {expiration.expirationType === ExpirationTypes.ExpirationPeriod ? (
                        <Tooltip text={expiration.expirationPeriod ?? t(l.NA)} position="bottom">
                            <div className="key-value-h">
                                <span className="ellipsis-text kv-label">{t(l.Period)}</span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {expiration.expirationPeriod ?? t(l.NA)}
                                </span>
                            </div>
                        </Tooltip>
                    ) : (
                        <>
                            <Tooltip text={expiration.expirationDateTime ?? t(l.NA)} position="bottom">
                                <div className="key-value-h">
                                    <span className="ellipsis-text kv-label">{t(l.Date)}</span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {expiration.expirationDateTime ?? t(l.NA)}
                                    </span>
                                </div>
                            </Tooltip>
                            <Tooltip text={expiration.expirationDateTime ?? t(l.NA)} position="bottom">
                                <div className="key-value-h">
                                    <span className="ellipsis-text kv-label">{t(l.TimeZone)}</span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {expiration.timezone ?? t(l.NA)}
                                    </span>
                                </div>
                            </Tooltip>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

GeneralInfo.propTypes = {
    templateId: PropTypes.number.isRequired,
    type: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    isPredefined: PropTypes.bool,
    additional: PropTypes.object,
    templateCategoryName: PropTypes.string,
    templateCategoryIds: PropTypes.array,
    predefinedKeyValue: PropTypes.object,
};

GeneralInfo.defaultProps = {
    isPredefined: false,
};

export default memo(GeneralInfo);
